import { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useProjectsList } from "../../../lib/react-query/projects";
import GanttCalendarView from "../../../components/Projects/GanttCalendarView";
import { CalendarOnboardingModal } from "../../../components/Calendar/CalendarOnboardingModal";
import { Spinner } from "../../../components/shared";
import { ProjectsIcon } from "../../../assets/icons";

const CalendarDemoModal = lazy(() => import("../../../components/Calendar/CalendarDemoModal"));

const CalendarPage = () => {
  const navigate = useNavigate();
  const [selectedProjectId, setSelectedProjectId] = useState("all");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const { data: projectsData, isLoading: isLoadingProjects } = useProjectsList(
    { ordering: "-updated_at", page_size: 100 },
    true
  );

  const projects = projectsData?.items || [];

  const isCrossProject = selectedProjectId === "all";
  const activeProjectId = isCrossProject ? null : Number(selectedProjectId);

  const isLoading = isLoadingProjects;

  return (
    <div className="flex flex-col gap-6 h-full py-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
        <div className="flex items-center gap-3">
          <ProjectsIcon width={28} height={28} className="text-primary" />
          <div>
            <h1 className="text-xl font-bold text-primary font-sans">Calendar</h1>
            <p className="text-sm text-mediumgrey">
              View and manage tasks across your projects
            </p>
          </div>
        </div>
        
        <div className="flex-1" />
        
        {/* Project Selector */}
        {projects.length > 0 && (
          <div className="flex w-full sm:w-auto items-center gap-2">
            <label htmlFor="calendar-project-filter" className="text-sm text-mediumgrey">Project:</label>
            <select
              id="calendar-project-filter"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="min-w-0 flex-1 sm:flex-none px-3 py-2 text-sm bg-white border border-surface-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary cursor-pointer"
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Help / Demo */}
        <button
          onClick={() => setShowDemoModal(true)}
          className="self-start shrink-0 p-2 rounded-lg border border-surface-border bg-white text-mediumgrey hover:text-secondary-dark hover:border-secondary/30 transition-all"
          aria-label="Open calendar guide"
          title="Calendar guide"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-mediumgrey">Loading...</p>
          </div>
        </div>
      ) : projects.length === 0 ? (
        <>
          {showOnboarding && (
            <CalendarOnboardingModal onDismiss={() => setShowOnboarding(false)} />
          )}
          <div className="flex-1 flex items-center justify-center min-h-[400px]">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 bg-surface-hover rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ProjectsIcon width={32} height={32} className="text-mediumgrey" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">No projects yet</h3>
              <p className="text-sm text-mediumgrey mb-4">
                Create a project to start using the calendar view.
              </p>
              <button
                onClick={() => navigate("/projects?prompt_project_create=1")}
                className="px-4 py-2 text-sm font-medium text-white bg-secondary hover:bg-secondary/90 rounded-lg transition-colors shadow-sm"
              >
                Create Project
              </button>
            </div>
          </div>
        </>
      ) : isCrossProject ? (
        <GanttCalendarView crossProject />
      ) : activeProjectId ? (
        <GanttCalendarView projectId={activeProjectId} />
      ) : null}

      {showDemoModal && (
        <Suspense fallback={null}>
          <CalendarDemoModal onDismiss={() => setShowDemoModal(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default CalendarPage;
